import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { importVesselRequirements, applyImportedRequirements } from '../../utils/regulatoryImport';

/**
 * Regulatory Requirements Import Component
 * Allows importing vessel requirements from the vessel calculator
 */
export default function RegulatoryImport({ onImportComplete }) {
  const [importStatus, setImportStatus] = useState(null);
  const [importedData, setImportedData] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file exported from the vessel calculator');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        processImport(jsonData);
      } catch (err) {
        setError('Invalid JSON file. Please use a file exported from the vessel calculator.');

      }
    };
    reader.readAsText(file);
  };

  // Process the imported data
  const processImport = (jsonData) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Parse and map the requirements
      const mapped = importVesselRequirements(jsonData);
      setImportedData(mapped);
      
      // Show preview
      setImportStatus('preview');
    } catch (err) {
      setError(`Import failed: ${err.message}`);

    } finally {
      setIsProcessing(false);
    }
  };

  // Apply the imported requirements
  const applyImport = async () => {
    if (!importedData) return;

    setIsProcessing(true);
    try {
      const results = await applyImportedRequirements(importedData);
      
      setImportStatus('complete');
      
      // Notify parent component
      if (onImportComplete) {
        onImportComplete(results);
      }

      // Show success message
      setTimeout(() => {
        setImportStatus(null);
        setImportedData(null);
      }, 5000);
    } catch (err) {
      setError(`Failed to apply requirements: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset the import
  const resetImport = () => {
    setImportStatus(null);
    setImportedData(null);
    setError(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Import Regulatory Requirements</h2>
        <a
          href="http://localhost:3000"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <FileText className="h-4 w-4" />
          Open Vessel Calculator
        </a>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-500"
          >
            ×
          </button>
        </div>
      )}

      {!importStatus && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Upload a JSON file exported from the Vessel Requirements Calculator
            </p>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
              disabled={isProcessing}
            />
            <label
              htmlFor="file-upload"
              className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
            >
              Choose File
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">How to use:</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Open the Vessel Requirements Calculator</li>
              <li>Configure your vessel details</li>
              <li>Click "Calculate Requirements"</li>
              <li>Click "Export Results" to download JSON</li>
              <li>Upload the JSON file here</li>
            </ol>
          </div>
        </div>
      )}

      {importStatus === 'preview' && importedData && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-900 mb-2">
              Ready to Import: {importedData.vesselConfig?.name || 'Vessel Requirements'}
            </h3>
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-green-800">
              <div>
                <span className="font-medium">Category:</span> {importedData.vesselConfig?.category}
              </div>
              <div>
                <span className="font-medium">Type:</span> {importedData.vesselConfig?.type}
              </div>
              <div>
                <span className="font-medium">Total Requirements:</span> {importedData.summary?.totalRequirements}
              </div>
              <div>
                <span className="font-medium">Mandatory:</span> {importedData.summary?.mandatoryCount}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-semibold text-gray-900">
                {importedData.equipment?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Equipment</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-semibold text-gray-900">
                {importedData.maintenance?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Maintenance</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-semibold text-gray-900">
                {(importedData.drills?.drills?.length || 0) + (importedData.drills?.training?.length || 0)}
              </div>
              <div className="text-sm text-gray-600">Drills & Training</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-semibold text-gray-900">
                {Object.values(importedData.documents || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0)}
              </div>
              <div className="text-sm text-gray-600">Documents</div>
            </div>
          </div>

          <div className="border-t pt-4 flex justify-between">
            <button
              onClick={resetImport}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={applyImport}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isProcessing ? 'Importing Requirements...' : 'Apply Requirements'}
            </button>
          </div>
        </div>
      )}

      {importStatus === 'complete' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
          <h3 className="mt-3 text-lg font-medium text-green-900">Import Successful!</h3>
          <p className="mt-2 text-sm text-green-700">
            Requirements have been imported to your SMS modules
          </p>
          <button
            onClick={resetImport}
            className="mt-4 px-4 py-2 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-md hover:bg-green-50"
          >
            Import Another
          </button>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-3">What gets imported:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            <div>
              <span className="font-medium">Equipment:</span> Safety equipment requirements
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            <div>
              <span className="font-medium">Maintenance:</span> Scheduled maintenance tasks
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            <div>
              <span className="font-medium">Drills:</span> Emergency drills & training
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
            <div>
              <span className="font-medium">Documents:</span> SMS policies, procedures & RAs
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}